import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '../../components/atoms/Modal';
import { apiFetch } from '../../stores/authStore';
import { formatLimitDisplay } from '../../lib/limits';
import {
  ONE_CLICK_CEILING_MAX,
  ONE_CLICK_CEILING_MIN,
  ATTACK_CEILING_MIN,
  ATTACK_CEILING_MAX,
  effectiveAttackCeiling,
  effectiveOneClickCeiling,
  type Sport,
  type TraderRules,
} from '../../types/shared/admin';

interface TraderSummary {
  id: string;
  username: string;
  maxContracts: number | null;
  maxPositions: number | null;
  traderRules: TraderRules | null;
}

interface CurrentUsage {
  openContracts: number;
  openPositions: number;
  intentContracts: number;
}

interface EditLimitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  trader: TraderSummary | null;
  /** Query string suffix for admin org switching, e.g. `?orgId=...`. */
  orgQueryParam: string;
  /** Optional live usage to render under the inputs ("Currently: …"). */
  currentUsage?: CurrentUsage;
  /** Called with the updated trader on success so the parent can refresh. */
  onSaved: (next: TraderSummary) => void;
}

// Org-standard defaults mirrored from the relay's POST /traders insert
// (relay/src/routes/manager.ts: DEFAULT_MAX_CONTRACTS / DEFAULT_MAX_POSITIONS).
// Pre-staged into the input value when the trader is currently Unlimited so
// unchecking the Unlimited toggle reveals a real, editable number instead
// of an empty input where the old placeholder "20" misled users into
// thinking the cap was already set.
const DEFAULT_MAX_POSITIONS = 20;
const DEFAULT_MAX_CONTRACTS = 10_000;

// Default trader_rules — matches relay/src/routes/manager.ts:DEFAULT_TRADER_RULES.
// New traders are sport-unrestricted, non-sports blocked, Polymarket allowed.
const DEFAULT_TRADER_RULES: TraderRules = {
  allowedSports: null,
  nonSportsEnabled: false,
  polymarketEnabled: true,
};

type FieldState = {
  unlimited: boolean;
  value: string; // raw input text so we can show "" without losing focus
};

function fieldFromCap(cap: number | null, defaultValue: number): FieldState {
  if (cap == null) {
    return { unlimited: true, value: String(defaultValue) };
  }
  return { unlimited: false, value: String(cap) };
}

export function parseLimitInputValue(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isSafeInteger(n) ? n : null;
}

interface RulesState {
  sportUnrestricted: boolean;
  /** Per-sport checkbox state. Keyed by sport string from server. */
  sportChecks: Record<string, boolean>;
  nonSportsEnabled: boolean;
  polymarketEnabled: boolean;
}

/** Parse the one-click ceiling box. Blank is kept distinct from malformed
 *  text; the save handler refuses both. Missing stored settings receive the
 *  relay's default, but clearing a configured value must not silently reset it.
 *
 *  This only works because the box is `type="text"`. A number input runs the
 *  HTML value-sanitization algorithm BEFORE any handler, and MEASURED in
 *  Chromium every one of `abc`, `2.5`, `1e`, `1.2.3`, `--3`, `1e9` typed into
 *  one leaves `value === ""`. Historically empty cleared the ceiling,
 *  so malformed text was indistinguishable from a deliberate clear:
 *  typing `2.5` over a ceiling of 5 silently SAVED the clear, with no error —
 *  and an absent ceiling restored the then-current V1 default of 500.
 *  A bounds check cannot rescue a number input; the raw text has to reach
 *  here. Same trap as the bot-defaults editor (#312). */
export function parseCeilingInputValue(value: string): number | null | 'invalid' {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  const n = Number(trimmed);
  if (!Number.isSafeInteger(n)) return 'invalid';
  if (n < ONE_CLICK_CEILING_MIN || n > ONE_CLICK_CEILING_MAX) return 'invalid';
  return n;
}

/** Parse the attack ceiling box — same text-input contract as
 *  parseCeilingInputValue above ('' = no explicit ceiling = attack orders are
 *  refused outright), with the tighter attack bounds. */
export function parseAttackCeilingInputValue(value: string): number | null | 'invalid' {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  const n = Number(trimmed);
  if (!Number.isSafeInteger(n)) return 'invalid';
  if (n < ATTACK_CEILING_MIN || n > ATTACK_CEILING_MAX) return 'invalid';
  return n;
}

function rulesStateFromRules(
  rules: TraderRules | null,
  allSports: readonly string[],
): RulesState {
  // If trader has no rules object (NULL column), treat as defaults applied
  // at edit time so the operator can shift away from "legacy unrestricted".
  const r = rules ?? DEFAULT_TRADER_RULES;
  const sportUnrestricted = r.allowedSports === null;
  const sportChecks: Record<string, boolean> = {};
  if (sportUnrestricted) {
    // When unrestricted, all checkboxes show as "checked" (visually all-on)
    // but the master toggle disables them.
    for (const s of allSports) sportChecks[s] = true;
  } else {
    const allowed = new Set<string>(r.allowedSports ?? []);
    for (const s of allSports) sportChecks[s] = allowed.has(s);
  }
  return {
    sportUnrestricted,
    sportChecks,
    nonSportsEnabled: r.nonSportsEnabled,
    polymarketEnabled: r.polymarketEnabled,
  };
}

/** What a NULL trader_rules column already permits — "legacy unrestricted".
 *
 *  Deliberately NOT DEFAULT_TRADER_RULES above, which blocks non-sport markets:
 *  that object is an EDIT-TIME default for the rules panel, offered so an
 *  operator working in that panel can shift a legacy trader onto real rules.
 *  Saving a size ceiling is not working in that panel, and materialising rules
 *  as a side effect of it would silently take away a permission the operator
 *  never touched. This matches the baseline useMemberGovernance uses for the
 *  capability chips, which have the same problem for the same reason. */
const UNRESTRICTED_EQUIVALENT_RULES: TraderRules = {
  allowedSports: null,
  nonSportsEnabled: true,
  polymarketEnabled: true,
};

/** The rules object for a save where the operator changed ONLY the ceiling and
 *  never opened the rules panel. Carries the trader's existing rules forward
 *  untouched — see rulesStateToRules for why omitting a field deletes it. */
function rulesWithCeiling(
  base: TraderRules | null,
  ceiling: number | null,
  attackCeiling: number | null,
): TraderRules {
  const next: TraderRules = { ...(base ?? UNRESTRICTED_EQUIVALENT_RULES) };
  if (ceiling == null) delete next.oneClickMaxContracts;
  else next.oneClickMaxContracts = ceiling;
  applyAttackCeilingEdit(next, base, attackCeiling);
  return next;
}

/** Preserve the existing effective ceiling when a one-click edit or default
 * materialization changes legacy arithmetic. Unchanged allowances retain mode. */
function applyAttackCeilingEdit(next: TraderRules, base: TraderRules | null, value: number | null): void {
  const legacyAllowanceChanged = base?.attackLimitMode === undefined &&
    effectiveAttackCeiling(next) !== effectiveAttackCeiling(base);
  if (value === effectiveAttackCeiling(base) && !legacyAllowanceChanged) return;
  if (value == null) delete next.attackMaxContracts;
  else next.attackMaxContracts = value;
  next.attackLimitMode = 'independent';
}

function rulesStateToRules(
  rs: RulesState,
  /** The trader's CURRENT rules. Load-bearing: see below. */
  base: TraderRules | null,
  ceiling: number | null,
  attackCeiling: number | null,
): TraderRules {
  // The relay REPLACES trader_rules wholesale on PATCH (relay/src/routes/
  // manager.ts) — it does not merge — so any field missing from this object is
  // deleted from the trader's record.
  //
  // This function used to build a fresh three-field object, so any save that
  // submitted rules at all — i.e. one where a RULE control was touched and
  // actually changed; a caps-only edit sends no traderRules and was never
  // affected — silently revoked the capability grants living in the same
  // column: oscarEnabled and oneClickEnabled, both reachable only from the
  // chips on the member card. Editing a trader's sport allowlist quietly took
  // away their Oscar access. Nothing tested it, because the chip toggles merge
  // correctly and only this path did not.
  //
  // Spreading `base` first fixes that for every sibling field at once,
  // including ones added after this was written — the four the modal owns are
  // overwritten immediately below, so the spread can only carry fields it does
  // not manage.
  const carried: TraderRules = {
    ...(base ?? {}),
    allowedSports: null,
    nonSportsEnabled: rs.nonSportsEnabled,
    polymarketEnabled: rs.polymarketEnabled,
  };
  if (!rs.sportUnrestricted) {
    // The sport list is server-provided as strings, but TraderRules narrows
    // to Sport[]. Server validates membership when the PATCH lands so it's
    // safe to cast here — the relay rejects unknown strings.
    carried.allowedSports = Object.entries(rs.sportChecks)
      .filter(([, on]) => on)
      .map(([s]) => s) as Sport[];
  }
  // An omitted key is what "no explicit ceiling" IS on the wire — writing 0 or
  // null instead would be rejected by parseTraderRulesField as invalid.
  if (ceiling == null) delete carried.oneClickMaxContracts;
  else carried.oneClickMaxContracts = ceiling;
  applyAttackCeilingEdit(carried, base, attackCeiling);
  return carried;
}

/** True when both rule objects represent the same effective state. */
function rulesEqual(a: TraderRules | null, b: TraderRules | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (a.nonSportsEnabled !== b.nonSportsEnabled) return false;
  if (a.polymarketEnabled !== b.polymarketEnabled) return false;
  // Absent and any number are different states, so normalise absent to null
  // rather than comparing `undefined` against a value.
  if ((a.oneClickMaxContracts ?? null) !== (b.oneClickMaxContracts ?? null)) return false;
  if ((a.attackMaxContracts ?? null) !== (b.attackMaxContracts ?? null)) return false;
  if (a.attackLimitMode !== b.attackLimitMode) return false;
  if (a.allowedSports === null && b.allowedSports === null) return true;
  if (a.allowedSports === null || b.allowedSports === null) return false;
  if (a.allowedSports.length !== b.allowedSports.length) return false;
  const setA = new Set(a.allowedSports);
  for (const s of b.allowedSports) if (!setA.has(s)) return false;
  return true;
}

export function shouldSubmitTraderRules(
  currentRules: TraderRules | null,
  nextRules: TraderRules,
  rulesTouched: boolean,
): boolean {
  return rulesTouched && !rulesEqual(nextRules, currentRules);
}

export function EditLimitsModal({
  isOpen,
  onClose,
  trader,
  orgQueryParam,
  currentUsage,
  onSaved,
}: EditLimitsModalProps) {
  const [contracts, setContracts] = useState<FieldState>({
    unlimited: true,
    value: String(DEFAULT_MAX_CONTRACTS),
  });
  const [positions, setPositions] = useState<FieldState>({
    unlimited: true,
    value: String(DEFAULT_MAX_POSITIONS),
  });
  const [allSports, setAllSports] = useState<readonly string[]>([]);
  // Its OWN state, reset on open beside `contracts`/`positions` — deliberately
  // NOT part of the derived rules draft below. That draft is keyed on the
  // fetched sport list and is discarded when the key moves, which happens the
  // first time the sport list lands. A ceiling typed in that window would have
  // been reverted with no sign on screen: exactly the "looks set, is not set"
  // failure this control exists to prevent.
  const [ceilingText, setCeilingText] = useState('');
  const [attackCeilingText, setAttackCeilingText] = useState('');

  // #349 — the rules panel is DERIVED from the trader plus the sport list, with
  // the operator's edits held against the exact (trader, sport-list) pair they
  // were made on. That removes the rebuild effect entirely while preserving its
  // behaviour precisely, including the discard: when the sport list lands, the
  // key changes, the draft stops matching and the derived value takes over —
  // which is what the old effect did by overwriting `rules` wholesale.
  const rulesKey = `${trader?.id ?? ''}|${allSports.join(',')}`;
  const [rulesDraft, setRulesDraft] = useState<{ key: string; value: RulesState } | null>(null);
  const rules =
    rulesDraft?.key === rulesKey
      ? rulesDraft.value
      : rulesStateFromRules(trader?.traderRules ?? null, allSports);
  const [rulesTouched, setRulesTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Fetch the sport list once — server-driven so we don't duplicate the
  // enum on the client. Cached for the lifetime of the modal's open state.
  const loadSportOptions = useCallback(async () => {
    try {
      const res = await apiFetch('/api/manager/sport-options');
      if (!res.ok) return;
      const data = (await res.json()) as { sports?: string[] };
      if (Array.isArray(data.sports)) setAllSports(data.sports);
    } catch {
      // Silent — the modal stays usable with the trader's existing rules
      // values; new sports just won't be visible until next open.
    }
  }, []);

  // Reset form state every time we open against a (potentially) different trader.
  //
  // #349 — suppressed deliberately; the two standard alternatives were tried and
  // measured, and both are worse than the effect:
  //
  //  * Remounting (keying the modal on trader id at the call site) resets this
  //    state for free, but `allSports` dies with the instance, so "Loading sport
  //    list…" flashes on EVERY reopen — including the Cancel-then-reopen path
  //    that is the only way the real UI reaches a second trader. Two tests in
  //    editLimitsModalReset.test.tsx fail under it; that is a visible regression.
  //  * Adjusting state during render (React's own alternative to a key) trades
  //    this warning for `react-hooks/set-state-in-render`, which is enabled and
  //    absent from lint-budget.json — an absent rule may not fire at all, so it
  //    fails the budget gate rather than passing it.
  //
  // Deriving these five values the way `rules` above is derived would mean
  // rewriting the form-state model across the save path, the validator and both
  // child components — a behaviour change disguised as cleanup, which this
  // ticket's non-goals rule out. The rules rebuild, which WAS containable, has
  // been derived instead of suppressed.
  useEffect(() => {
    if (!isOpen || !trader) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    setContracts(fieldFromCap(trader.maxContracts, DEFAULT_MAX_CONTRACTS));
    setPositions(fieldFromCap(trader.maxPositions, DEFAULT_MAX_POSITIONS));
    // Show the effective default for absent settings. Existing stored values
    // are never rewritten merely by opening this form. Invalid values remain
    // visible for correction rather than being disguised as defaults.
    setCeilingText(
      trader.traderRules?.oneClickMaxContracts !== undefined
        ? String(trader.traderRules.oneClickMaxContracts)
        : String(effectiveOneClickCeiling(trader.traderRules)),
    );
    setAttackCeilingText(
      effectiveAttackCeiling(trader.traderRules) != null
        ? String(effectiveAttackCeiling(trader.traderRules))
        : '',
    );
    setRulesTouched(false);
    setError(null);
    setSaving(false);
    void loadSportOptions();
  }, [isOpen, trader, loadSportOptions]);

  if (!trader) return null;

  const updateRules = (next: RulesState | ((prev: RulesState) => RulesState)) => {
    // Functional form preserved: read the previous draft inside the updater
    // rather than closing over the render-time `rules`, so two updates batched
    // into one tick still compose instead of the second discarding the first.
    // A draft under a stale key is ignored — that is the discard-on-rebuild
    // behaviour, and it must apply here too, not just on the render path.
    setRulesDraft((prev) => {
      const current = prev?.key === rulesKey ? prev.value : rules;
      return { key: rulesKey, value: typeof next === 'function' ? next(current) : next };
    });
    setRulesTouched(true);
  };

  const parseField = (f: FieldState, label: string): number | null | 'invalid' => {
    if (f.unlimited) return null;
    const n = parseLimitInputValue(f.value);
    if (n == null) {
      setError(`${label} must be a whole non-negative integer or set to Unlimited`);
      return 'invalid';
    }
    return n;
  };

  const handleSave = async () => {
    setError(null);
    const c = parseField(contracts, 'Max contracts');
    if (c === 'invalid') return;
    const p = parseField(positions, 'Max positions');
    if (p === 'invalid') return;

    // Sport save guard: rules unchecked AND not unrestricted = block.
    // Mirrors the relay's empty-allowedSports rejection so the user sees a
    // friendly message before the round-trip.
    if (!rules.sportUnrestricted) {
      const anyChecked = Object.values(rules.sportChecks).some(Boolean);
      if (!anyChecked) {
        setError(
          'Pick at least one sport, or check "Unrestricted" to remove the sport allowlist.',
        );
        return;
      }
    }

    const ceiling = parseCeilingInputValue(ceilingText);
    if (ceiling === 'invalid' || ceiling == null) {
      setError(
        `One-click order-size ceiling must be a whole number from ${ONE_CLICK_CEILING_MIN} to `
          + `${ONE_CLICK_CEILING_MAX}. Use the access toggle to disable trading.`,
      );
      return;
    }
    const attackCeiling = parseAttackCeilingInputValue(attackCeilingText);
    if (attackCeiling === 'invalid' || attackCeiling == null) {
      setError(
        `Attack order-size ceiling must be a whole number from ${ATTACK_CEILING_MIN} to `
          + `${ATTACK_CEILING_MAX}. Use the Attack toggle to disable Attack.`,
      );
      return;
    }

    // Two different objects, because the two edits mean different things on a
    // legacy NULL-rules trader: working in the rules panel materialises the
    // edit-time defaults on purpose, while a ceiling-only save must not.
    const nextRules = rulesTouched
      ? rulesStateToRules(rules, trader.traderRules, ceiling, attackCeiling)
      : rulesWithCeiling(trader.traderRules, ceiling, attackCeiling);
    const ceilingChanged =
      effectiveOneClickCeiling(trader.traderRules) !== ceiling ||
      effectiveAttackCeiling(trader.traderRules) !== attackCeiling;

    // Only send fields that actually changed — keeps the audit trail clean
    // and the PATCH no-op-safe if the user opens and closes without edits.
    const body: Record<string, unknown> = {};
    if (c !== trader.maxContracts) body.maxContracts = c;
    if (p !== trader.maxPositions) body.maxPositions = p;
    // `shouldSubmitTraderRules` gates on the rules panel having been touched,
    // so the ceiling needs its own term or a ceiling-only edit would be dropped
    // silently. Both false still means no traderRules key at all, which is what
    // keeps an untouched legacy NULL column null.
    if (ceilingChanged || shouldSubmitTraderRules(trader.traderRules, nextRules, rulesTouched)) {
      body.traderRules = nextRules;
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch(`/api/manager/traders/${trader.id}${orgQueryParam}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError((data as { error?: string }).error ?? 'Failed to save limits');
        setSaving(false);
        return;
      }
      const updated = (data as { user?: TraderSummary }).user ?? {
        ...trader,
        maxContracts: c,
        maxPositions: p,
        traderRules: nextRules,
      };
      onSaved(updated);
      onClose();
    } catch {
      setError('Network error');
      setSaving(false);
    }
  };

  const usageLine = currentUsage ? (
    <p className="text-muted-foreground text-xs">
      Currently: {currentUsage.openPositions.toLocaleString()} positions ·{' '}
      {currentUsage.openContracts.toLocaleString()} contracts open
      {currentUsage.intentContracts > 0
        ? ` (+${currentUsage.intentContracts.toLocaleString()} in resting buys)`
        : ''}
    </p>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Edit limits — ${trader.username}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="bg-muted hover:bg-muted/80 rounded-lg px-4 py-2 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save changes
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {usageLine}

        <LimitField
          label="Max positions"
          help="Hard cap on count of distinct open (ticker, side) keys."
          field={positions}
          onChange={setPositions}
          defaultHint="Whole positive integer — e.g. 20 distinct positions"
        />

        <LimitField
          label="Max contracts"
          help="Hard cap on Σ open contracts across unsettled markets, both venues."
          field={contracts}
          onChange={setContracts}
          defaultHint="Whole positive integer — e.g. 10,000 contracts"
        />

        <CeilingField value={ceilingText} onChange={setCeilingText} />

        <AttackCeilingField value={attackCeilingText} onChange={setAttackCeilingText} />

        <SportsSection
          allSports={allSports}
          state={rules}
          onChange={updateRules}
        />

        <ToggleRow
          label="Allow non-sport markets"
          help="Politics, crypto, and other non-sport Kalshi markets. Independent of the sport allowlist."
          checked={rules.nonSportsEnabled}
          onChange={(v) => updateRules((s) => ({ ...s, nonSportsEnabled: v }))}
        />

        <ToggleRow
          label="Allow new Polymarket buys"
          help="Sells and cancels always pass — this only gates opening new Polymarket exposure."
          checked={rules.polymarketEnabled}
          onChange={(v) => updateRules((s) => ({ ...s, polymarketEnabled: v }))}
        />

        {error && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
        )}

        <p className="text-muted-foreground border-border rounded-md border bg-muted/20 px-3 py-2 text-[11px]">
          Current caps: positions {formatLimitDisplay(trader.maxPositions)}, contracts{' '}
          {formatLimitDisplay(trader.maxContracts)}, one-click ceiling{' '}
          {effectiveOneClickCeiling(trader.traderRules) != null
            ? `${effectiveOneClickCeiling(trader.traderRules)} contracts`
            : 'invalid'}
          , attack ceiling{' '}
          {effectiveAttackCeiling(trader.traderRules) != null
            ? `${effectiveAttackCeiling(trader.traderRules)} contracts`
            : 'invalid'}
          . Unlimited applies only to position and total-contract caps, not the
          one-click or Attack order-size ceilings.
          Sells and cancels always pass regardless of any rule above.
        </p>
      </div>
    </Modal>
  );
}

interface LimitFieldProps {
  label: string;
  help: string;
  field: FieldState;
  onChange: (next: FieldState) => void;
  defaultHint: string;
}

function LimitField({ label, help, field, onChange, defaultHint }: LimitFieldProps) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <label className="text-foreground text-xs font-medium">{label}</label>
        <label className="text-muted-foreground inline-flex items-center gap-1.5 text-[11px]">
          <input
            type="checkbox"
            checked={field.unlimited}
            onChange={(e) => onChange({ ...field, unlimited: e.target.checked })}
            className="accent-blue-500"
          />
          Unlimited
        </label>
      </div>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        value={field.value}
        disabled={field.unlimited}
        onChange={(e) => onChange({ ...field, value: e.target.value })}
        placeholder={defaultHint}
        className="bg-background border-border text-foreground w-full rounded-md border px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50"
      />
      <p className="text-muted-foreground mt-1 text-[11px]">{help}</p>
    </div>
  );
}

/** The explicit per-order ceiling for Attack (W/E) orders
 *  (traderRules.attackMaxContracts). Same text-input contract as CeilingField
 *  below — empty is "no explicit ceiling", which is a refusal for attack
 *  orders rather than permission. */
function AttackCeilingField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div>
      <label htmlFor="attack-ceiling" className="text-foreground mb-1 block text-xs font-medium">
        Attack order-size ceiling
      </label>
      <input
        id="attack-ceiling"
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter a contract limit"
        className="bg-background border-border text-foreground w-full rounded-md border px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
      />
      <p className="text-muted-foreground mt-1 text-[11px]">
        Maximum contracts per Attack order. Saving a changed limit makes it independent
        of one-click sizing. If no Attack limit is saved, it defaults to 1,000
        independently of one-click. Existing limits are preserved. Turn Attack
        off with its access toggle, not by clearing this field. Attack may incur taker fees.
      </p>
    </div>
  );
}

/** The effective per-order ceiling for armed one-click and Live Trading V2
 *  (traderRules.oneClickMaxContracts).
 *
 *  Deliberately NOT a LimitField: that control pairs its box with an
 *  "Unlimited" checkbox, and unlimited is precisely the state this field must
 *  never be able to express. The form displays defaults for missing stored
 *  settings, and refuses empty edits. Access is controlled by the grant. */
function CeilingField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div>
      <label
        htmlFor="one-click-ceiling"
        className="text-foreground mb-1 block text-xs font-medium"
      >
        One-click order-size ceiling
      </label>
      {/* type="text", NOT number — see parseCeilingInputValue. Costs the
          spinner arrows; `inputMode` keeps the numeric keypad on touch.

          The two cap fields above keep `type="number"` because the same
          sanitization is harmless there, and the difference is WHAT BLANK
          MEANS, not the input type. On those fields blank is itself an error
          (measured: emptying Max contracts with Unlimited unchecked refuses
          the save with "must be a whole non-negative integer or set to
          Unlimited", and sends nothing), so a value the browser sanitized away
          can never be mistaken for one the operator intended. On THIS field
          blank is a legitimate instruction — clear the ceiling — so the two
          are indistinguishable, which is the whole defect. */}
      <input
        id="one-click-ceiling"
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter a contract limit"
        className="bg-background border-border text-foreground w-full rounded-md border px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
      />
      <p className="text-muted-foreground mt-1 text-[11px]">
        Whole contracts, {ONE_CLICK_CEILING_MIN}–{ONE_CLICK_CEILING_MAX}. Maximum per ordinary
        one-click or passive hotkey order, separate from position caps. Defaults to 10,000
        only when no limit is saved. Existing limits are preserved. Access toggles
        control whether trading is enabled; turning access off retains this limit.
      </p>
    </div>
  );
}

interface SportsSectionProps {
  allSports: readonly string[];
  state: RulesState;
  onChange: (next: RulesState) => void;
}

function SportsSection({ allSports, state, onChange }: SportsSectionProps) {
  const setUnrestricted = (v: boolean) => {
    if (v) {
      // Turning Unrestricted ON: reset checks to all-on so the visual
      // matches the underlying "all sports allowed" state.
      const allOn: Record<string, boolean> = {};
      for (const s of allSports) allOn[s] = true;
      onChange({ ...state, sportUnrestricted: true, sportChecks: allOn });
    } else {
      // Turning OFF: keep current checks; user picks the subset.
      onChange({ ...state, sportUnrestricted: false });
    }
  };
  const toggleSport = (sport: string, v: boolean) => {
    onChange({
      ...state,
      sportChecks: { ...state.sportChecks, [sport]: v },
    });
  };
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <label className="text-foreground text-xs font-medium">Allowed sports</label>
        <label className="text-muted-foreground inline-flex items-center gap-1.5 text-[11px]">
          <input
            type="checkbox"
            checked={state.sportUnrestricted}
            onChange={(e) => setUnrestricted(e.target.checked)}
            className="accent-blue-500"
          />
          Unrestricted
        </label>
      </div>
      <div
        className={`border-border bg-background grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-md border px-3 py-2 sm:grid-cols-3 ${
          state.sportUnrestricted ? 'opacity-50' : ''
        }`}
      >
        {allSports.length === 0 ? (
          <p className="text-muted-foreground col-span-full text-xs">
            Loading sport list…
          </p>
        ) : (
          allSports.map((sport) => (
            <label
              key={sport}
              className="text-foreground inline-flex items-center gap-1.5 text-xs"
            >
              <input
                type="checkbox"
                checked={!!state.sportChecks[sport]}
                disabled={state.sportUnrestricted}
                onChange={(e) => toggleSport(sport, e.target.checked)}
                className="accent-blue-500"
              />
              {sport}
            </label>
          ))
        )}
      </div>
      <p className="text-muted-foreground mt-1 text-[11px]">
        Note: NCAA includes men&apos;s, women&apos;s, and generic basketball markets;
        Tennis covers ATP and WTA. Uncheck Unrestricted to limit to a subset.
      </p>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  help: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}

function ToggleRow({ label, help, checked, onChange }: ToggleRowProps) {
  return (
    <div>
      <label className="text-foreground inline-flex items-center gap-2 text-xs font-medium">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-blue-500"
        />
        {label}
      </label>
      <p className="text-muted-foreground mt-1 text-[11px]">{help}</p>
    </div>
  );
}
